import { useEffect, useRef, useState } from "react";
import { Sparkles, ArrowRight, ArrowLeft, UploadCloud, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  STUDIO_EXAMPLE_BEFORE,
  STUDIO_EXAMPLE_AFTERS,
} from "@/pages/GenerateProductCatalog/constants";
import type { UploadedReferenceImage } from "@/lib/types/media";
import { ACCEPTED_IMAGE_TYPES } from "@/config/media";
import { useImageUpload } from "@/hooks/useImageUpload";

const MAX_ADDITIONAL_IMAGES = 5;

interface StudioHeroProps {
  /** Public URL or object URL of the user's uploaded product photo (null = no upload yet). */
  uploadPreview: string | null;
  isUploading: boolean;
  isGenerating: boolean;
  /** User's generated results (the 4 studio photos). May be 0–4 in flight. */
  results: string[];
  expectedResultCount: number;
  /** True when the user has completed all required steps and can hit generate. */
  canGenerate: boolean;
  /** DOM id to scroll to when the placeholder CTA is clicked while not yet ready. */
  presetsAnchorId: string;
  /** Additional reference images to feed the AI (improves output quality). */
  additionalImages: UploadedReferenceImage[];
  onAdditionalImagesChange: (images: UploadedReferenceImage[]) => void;
  onSelectFile: (file: File) => void;
  onClearUpload: () => void;
  onGenerate: () => void;
}

export function StudioHero({
  uploadPreview,
  isUploading,
  isGenerating,
  results,
  expectedResultCount,
  canGenerate,
  presetsAnchorId,
  additionalImages,
  onAdditionalImagesChange,
  onSelectFile,
  onClearUpload,
  onGenerate,
}: StudioHeroProps) {
  const { t, isRTL } = useLanguage();
  const hasUpload = !!uploadPreview;
  // Show the "create" CTA + caption below the after-stack only when the user
  // has uploaded but no results have streamed in yet (and we're not in a
  // generation in flight — the spinners take over then).
  const showAfterCta = hasUpload && results.length === 0 && !isGenerating;

  return (
    <div className="rounded-3xl border border-border bg-gradient-to-b from-brand/[0.05] to-transparent p-5 md:p-8">
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-2 md:mb-3 bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
          {t.productStudio.heroTitle}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
          {t.productStudio.heroSubtitle}
        </p>
      </div>

      {hasUpload ? (
        // Post-upload: compact transformation showcase + upload-management strip
        <div className="space-y-4 md:space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="max-w-md mx-auto w-full">
            <TransformationShowcase
              beforeImage={uploadPreview}
              afterImages={results}
              expectedAfterCount={expectedResultCount}
              isGenerating={isGenerating}
              isRTL={isRTL}
              autoRotate={false}
              afterCta={
                showAfterCta
                  ? {
                      caption: t.productStudio.afterPlaceholder,
                      ctaLabel: t.productStudio.createNowCta,
                      canGenerate,
                      presetsAnchorId,
                      onGenerate,
                    }
                  : undefined
              }
            />
          </div>
          <div className="max-w-md mx-auto w-full">
            <UploadStrip
              uploadPreview={uploadPreview}
              isUploading={isUploading}
              additionalImages={additionalImages}
              onAdditionalImagesChange={onAdditionalImagesChange}
              onClearUpload={onClearUpload}
            />
          </div>
        </div>
      ) : (
        // Initial: side-by-side example showcase + big drop-zone
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-center">
          <TransformationShowcase
            beforeImage={STUDIO_EXAMPLE_BEFORE}
            afterImages={STUDIO_EXAMPLE_AFTERS as unknown as string[]}
            expectedAfterCount={STUDIO_EXAMPLE_AFTERS.length}
            isGenerating={false}
            isRTL={isRTL}
            autoRotate
            afterCta={undefined}
          />
          <UploadDropzone isUploading={isUploading} onSelectFile={onSelectFile} />
        </div>
      )}
    </div>
  );
}

interface AfterCta {
  caption: string;
  ctaLabel: string;
  canGenerate: boolean;
  presetsAnchorId: string;
  onGenerate: () => void;
}

function TransformationShowcase({
  beforeImage,
  afterImages,
  expectedAfterCount,
  isGenerating,
  isRTL,
  autoRotate,
  afterCta,
}: {
  beforeImage: string;
  afterImages: string[];
  expectedAfterCount: number;
  isGenerating: boolean;
  isRTL: boolean;
  autoRotate: boolean;
  afterCta: AfterCta | undefined;
}) {
  const { t } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 md:gap-4">
      {/* BEFORE — single product photo */}
      <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border bg-card">
        <img
          src={beforeImage}
          alt={t.productStudio.heroBeforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
          <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
            {t.productStudio.heroBeforeLabel}
          </span>
        </div>
      </div>

      {/* Arrow — vertically centered relative to the cards (cards are aspect-[4/5]) */}
      <div className="aspect-[4/5] flex items-center shrink-0">
        <div className="w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center">
          <Arrow className="w-4 h-4 text-brand" />
        </div>
      </div>

      {/* AFTER — 4 cards stacked with slight overlap, optional CTA below */}
      <div className="flex flex-col">
        <AfterStack
          afterImages={afterImages}
          expectedCount={expectedAfterCount}
          isGenerating={isGenerating}
          label={t.productStudio.heroAfterLabel}
          autoRotate={autoRotate}
        />

        {afterCta && (
          <div className="mt-4 flex flex-col items-center gap-2 text-center">
            <button
              type="button"
              onClick={() => {
                if (afterCta.canGenerate) {
                  afterCta.onGenerate();
                  return;
                }
                document
                  .getElementById(afterCta.presetsAnchorId)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={cn(
                "inline-flex items-center justify-center gap-2 px-4 md:px-5 h-11 md:h-12 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm md:text-base font-bold whitespace-nowrap transition-all",
                "shadow-[0_0_20px_hsl(var(--brand)/0.35),0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_0_28px_hsl(var(--brand)/0.55)] hover:scale-[1.04] active:scale-[0.97]",
              )}
            >
              <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
              <span>{afterCta.ctaLabel}</span>
            </button>
            <span className="text-sm md:text-base font-semibold text-foreground">
              {afterCta.caption}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const AFTER_STACK_ROTATE_INTERVAL_MS = 2500;

/** A 4-card "fanned-out" stack, each card slightly offset and rotated, to
 * communicate "you'll get multiple results" in a small footprint.
 *
 * When `autoRotate` is true (used for the initial-state example showcase),
 * the stack cycles through the images so the viewer can see all angles.
 * Hover/click pauses the auto-rotation; clicking advances manually. */
function AfterStack({
  afterImages,
  expectedCount,
  isGenerating,
  label,
  autoRotate,
}: {
  afterImages: string[];
  expectedCount: number;
  isGenerating: boolean;
  label: string;
  autoRotate: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoRotate || isPaused || expectedCount <= 1) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % expectedCount);
    }, AFTER_STACK_ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [autoRotate, isPaused, expectedCount]);

  const handleAdvance = () => {
    if (expectedCount <= 1) return;
    setActiveIndex((i) => (i + 1) % expectedCount);
  };

  const slots = Array.from({ length: expectedCount });
  const canCycle = expectedCount > 1 && afterImages.length === expectedCount;

  return (
    <div
      className={cn("relative aspect-[4/5]", canCycle && "cursor-pointer")}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onClick={canCycle ? handleAdvance : undefined}
      role={canCycle ? "button" : undefined}
      tabIndex={canCycle ? 0 : undefined}
      aria-label={canCycle ? label : undefined}
      onKeyDown={(e) => {
        if (canCycle && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleAdvance();
        }
      }}
    >
      {slots.map((_, i) => {
        // Stack position relative to the active card: 0 = front, N-1 = back.
        const stackPosFromFront = (activeIndex - i + expectedCount) % expectedCount;
        // Visual slot: N-1 = front of fan, 0 = back. Offset/rotation are
        // centered around the visual slot so the fan spreads symmetrically.
        const visualSlot = expectedCount - 1 - stackPosFromFront;
        const offset = (visualSlot - (expectedCount - 1) / 2) * 8;
        const rotate = (visualSlot - (expectedCount - 1) / 2) * 2.5;
        const opacity = 0.75 + (visualSlot / Math.max(expectedCount - 1, 1)) * 0.25;
        const image = afterImages[i];
        const isFrontCard = stackPosFromFront === 0;

        return (
          <div
            key={i}
            className="absolute inset-0 rounded-2xl overflow-hidden border border-border bg-card shadow-sm transition-all duration-500 ease-out"
            style={{
              transform: `translateX(${offset}px) rotate(${rotate}deg)`,
              zIndex: visualSlot + 1,
              opacity,
            }}
          >
            {image ? (
              <img
                src={image}
                alt={`Result ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
                {isGenerating ? (
                  <Loader2 className="w-6 h-6 text-brand/50 animate-spin" />
                ) : (
                  <Sparkles className="w-6 h-6 text-brand/30" />
                )}
              </div>
            )}
            {isFrontCard && (
              <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
                <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
                  {label}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UploadDropzone({
  isUploading,
  onSelectFile,
}: {
  isUploading: boolean;
  onSelectFile: (file: File) => void;
}) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onSelectFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
    if (file) onSelectFile(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        disabled={isUploading}
        className={cn(
          "w-full h-full min-h-[260px] md:min-h-[360px] rounded-2xl border-2 border-dashed bg-card/40 flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all duration-200 text-center",
          isDragging
            ? "border-brand bg-brand/5 scale-[1.01]"
            : "border-border hover:border-brand/50 hover:bg-card",
          isUploading && "opacity-60 cursor-wait",
        )}
      >
        {isUploading ? (
          <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
        ) : (
          <>
            <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center">
              <UploadCloud className="w-8 h-8 text-brand" />
            </div>
            <div>
              <p className="text-base md:text-lg font-semibold text-foreground">
                {t.productStudio.uploadCta}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {t.productStudio.uploadHint}
              </p>
            </div>
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              {t.productStudio.uploadFormats}
            </p>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </>
  );
}

function UploadStrip({
  uploadPreview,
  isUploading,
  additionalImages,
  onAdditionalImagesChange,
  onClearUpload,
}: {
  uploadPreview: string;
  isUploading: boolean;
  additionalImages: UploadedReferenceImage[];
  onAdditionalImagesChange: (images: UploadedReferenceImage[]) => void;
  onClearUpload: () => void;
}) {
  const { t } = useLanguage();
  const { normalizeFilesOrWarn } = useImageUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const slotsLeft = MAX_ADDITIONAL_IMAGES - additionalImages.length;
  const canAddMore = slotsLeft > 0;

  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      addFilesToAdditional(files);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const addFilesToAdditional = async (rawFiles: File[]) => {
    const files = await normalizeFilesOrWarn(rawFiles);
    if (!files) return;

    const accepted = files
      .filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type))
      .slice(0, slotsLeft);
    if (accepted.length === 0) return;

    accepted.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const url = event.target?.result as string;
        const newImage: UploadedReferenceImage = {
          id: `add-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          url,
          file,
        };
        onAdditionalImagesChange([...additionalImages, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveAdditional = (id: string) => {
    onAdditionalImagesChange(additionalImages.filter((img) => img.id !== id));
  };

  return (
    <div>
      <h3 className="text-sm md:text-base font-semibold text-foreground">
        {t.productStudio.addMorePhotos}
      </h3>
      <p className="text-xs text-muted-foreground mt-0.5 mb-2">
        {t.productStudio.addMorePhotosHint}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Primary product thumbnail */}
        <div className="relative w-20 h-20 rounded-2xl overflow-hidden border border-border bg-card shrink-0">
          <img
            src={uploadPreview}
            alt={t.productStudio.uploadTitle}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={onClearUpload}
            aria-label="Remove"
            className="absolute top-1 end-1 w-6 h-6 bg-background/90 hover:bg-background rounded-full flex items-center justify-center shadow-md"
          >
            <X className="w-3.5 h-3.5 text-foreground" />
          </button>
        </div>

        {/* Additional reference image thumbnails */}
        {additionalImages.map((img) => (
          <div
            key={img.id}
            className="relative w-20 h-20 rounded-2xl overflow-hidden border border-border bg-card shrink-0"
          >
            <img
              src={img.url}
              alt="Reference"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => handleRemoveAdditional(img.id)}
              aria-label="Remove"
              className="absolute top-1 end-1 w-6 h-6 bg-background/90 hover:bg-background rounded-full flex items-center justify-center shadow-md"
            >
              <X className="w-3.5 h-3.5 text-foreground" />
            </button>
          </div>
        ))}

        {/* Add-more button */}
        {canAddMore && (
          <button
            type="button"
            onClick={openPicker}
            disabled={isUploading}
            className={cn(
              "w-20 h-20 rounded-2xl border-2 border-dashed bg-card flex flex-col items-center justify-center gap-1 text-xs font-medium text-muted-foreground transition-all shrink-0",
              "hover:border-brand/40 hover:text-foreground",
              isUploading && "opacity-60 cursor-wait",
            )}
          >
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <UploadCloud className="w-5 h-5" />
                <span>+</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
